import {useCallback, useMemo, useState} from "react";
import axios from "axios";
import moment from "moment";
import {isEqual} from "lodash";
import {bookingAPI} from "../common/api/bookingAPI";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {
    API_BOOKING_EMAIL_GUEST,
    API_PREARRIVAL,
    API_PREARRIVAL_CO_TRAVELLER,
    API_PREARRIVAL_CO_TRAVELLERS,
    API_PREARRIVAL_SAVE,
    API_RESERVATIONS,
    API_RESERVATIONS_ARRIVAL_CARD
} from "../common/api/apiConstants";
import * as Sentry from "@sentry/react";

export const useArrivalCard = () => {
    const {initialized, authenticated} = useKeycloakFunctions();

    // Internal state
    const [maxGuests, setMaxGuests] = useState();
    const [arrivalCardResData, setArrivalCardResData] = useState();
    const [reservationSignature, setReservationSignature] = useState();

    // Exposed state
    const [guests, setGuests] = useState();
    const [arrivalTime, setArrivalTime] = useState();
    const [wishListItems, setWishListItems] = useState([]);

    const [initLoading, setInitLoading] = useState(true);
    const [operationLoading, setOperationLoading] = useState();
    const [asyncOperationsLoading, setAsyncOperationLoading] = useState([]);
    const [operationMetadata, setOperationMetadata] = useState([]);

    const [initError, setInitError] = useState();
    const [operationError, setOperationError] = useState();
    const [operationSuccess, setOperationSuccess] = useState();

    const ARRIVAL_CARD_OPERATION_TYPE = {
        INIT: "init",
        TOO_MANY_POTENTIAL_GUESTS: "too_many_potential_guests",
        SAVE_ARRIVAL_CARD: "save_arrival_card",
        UPDATE_CO_TRAVELLER: "update_co_traveller",
        CREATE_CO_TRAVELLER: "create_co_traveller",
        DELETE_CO_TRAVELLER: "delete_co_traveller",
        SAVE_WISHLIST: "save_wishlist",
        SAVE_ARRIVAL_TIME: "save_arrival_time",
        ADD_GUEST: "add_guest",
        REMOVE_GUEST: "remove_guest",
        CHANGE_MAIN_GUEST: "change_main_guest"
    };

    const GUEST_ACTION = {
        CHANGE_MAIN_GUEST: "change_main_guest",
        ADD_NEW_GUEST: "add_new_guest",
        CREATE_GUEST: "create_guest"
    };

    // Internal hooks
    const pushMetadataItem = useCallback((item, id) => {
        setOperationMetadata(prev => [...prev, {
            ...item,
            id
        }]);
    }, [setOperationMetadata]);

    const removeMetadataItem = useCallback((itemId) => {
        setOperationMetadata(prev => prev.filter((item) => item?.id !== itemId));
    }, [setOperationMetadata]);

    const save = useCallback((reservationId, data) => {
        return bookingAPI.post(
            API_PREARRIVAL + "/" + reservationId + API_PREARRIVAL_SAVE,
            data,
            { headers: {
                "x-reservation-signature": reservationSignature
            }}
        );
    }, [reservationSignature]);

    const sendEmailToGuest = (reservationId, guestId) => {
        bookingAPI.post(API_BOOKING_EMAIL_GUEST, {
            reservation_id: reservationId,
            guest_id: guestId
        }).catch(err => {
            console.error(err);
            Sentry.captureException(err.response);
        });
    };

    // External hooks
    const initArrivalCard = useCallback(
        async (reservationId, reservationSignature, locale, reservationMaxGuests, reservationObjectId, arrivalTime) => {
            setMaxGuests(reservationMaxGuests);
            setReservationSignature(reservationSignature);

            if (initialized && authenticated) {
                try {
                    const res = await axios.all([
                        bookingAPI.get(`${API_RESERVATIONS}/${reservationId}${API_RESERVATIONS_ARRIVAL_CARD}`, {
                            params: {
                                locale,
                                ...(reservationObjectId ? {reservation_object_id: reservationObjectId} : {})
                            },
                            headers: {
                                "x-reservation-signature": reservationSignature
                            },
                            redirectOnError: false,
                        }),

                        bookingAPI.get(`${API_PREARRIVAL}${API_PREARRIVAL_CO_TRAVELLERS}`, {
                            redirectOnError: true,
                        })
                    ]);

                    const arrivalCardData = res?.[0]?.data;
                    const coTravellersData = res?.[1]?.data;

                    setArrivalTime(arrivalTime !== undefined
                        ? arrivalTime?.substring(0, arrivalTime?.lastIndexOf(":"))
                        : arrivalCardData?.arrival_time?.substring(0, arrivalCardData?.arrival_time.lastIndexOf(":"))
                    );
                    if (arrivalCardData?.checklist?.length > 0) {
                        setWishListItems(arrivalCardData.checklist);
                    }

                    const tempGuests = [];
                    for (const ct of coTravellersData) {
                        // check if inside reservation
                        let guest = {};

                        let guestCoTraveller;
                        const guestCoTravellerIndex = arrivalCardData?.guests.findIndex((g) => g.object_id === ct.object_id);

                        if (guestCoTravellerIndex > -1) {
                            guestCoTraveller = arrivalCardData?.guests[guestCoTravellerIndex];
                        }

                        if (guestCoTraveller) {
                            guest = {
                                ...guestCoTraveller,
                                ...ct,
                                selected: true,
                                in_reservation: true,
                                potential_guest: true,
                                is_editable: true,
                                is_cotraveller: true,
                                position: guestCoTravellerIndex
                            };
                        } else {
                            guest = {
                                ...ct,
                                selected: false,
                                in_reservation: false,
                                potential_guest: false,
                                is_editable: true,
                                is_cotraveller: true,
                            };
                        }

                        tempGuests.push(guest);
                    }

                    // Handle guests that have been added manually by hapimag employee
                    for (let i = 0; i < arrivalCardData?.guests?.length; i ++) {
                        const guest = arrivalCardData?.guests[i];
                        if (
                            !guest?.object_id ||
                            !coTravellersData?.filter(ct => ct?.object_id === guest.object_id).length
                        ) {
                            tempGuests.push({
                                ...guest,
                                selected: true,
                                in_reservation: true,
                                potential_guest: false,
                                is_editable: false,
                                is_cotraveller: false,
                                position: i
                            });
                        }
                    }

                    // Main guest to be on top of the list
                    setGuests(tempGuests.reverse().sort((a, b) => Boolean(b.main_guest) - Boolean(a.main_guest)));
                    // Used for monitoring the changes
                    setArrivalCardResData({
                        guests: tempGuests,
                        arrivalTime: arrivalTime
                            ? arrivalTime?.substring(0, arrivalTime?.lastIndexOf(":"))
                            : arrivalCardData?.arrival_time?.substring(0, arrivalCardData?.arrival_time.lastIndexOf(":")),
                        wishListItems: arrivalCardData.checklist
                    });
                    setInitLoading(false);
                } catch (err) {
                    setInitError(err);
                    setInitLoading(false);
                }
            }
        },
        [initialized, authenticated]
    );

    const saveArrivalCard = (reservationId, reservationObjectId) => {
        if (initialized && authenticated) {
            setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_CARD);

            const participatingGuestsIds = [];
            for (const g of guests) {
                if (g?.in_reservation && g?.selected) {
                    participatingGuestsIds.push(g?.object_id);
                }
            }

            save(reservationId, {
                guests: participatingGuestsIds,
                main_guest: guests?.find((g) => g.main_guest)?.object_id.toString(),
                arrival_time: !isEqual(arrivalCardResData?.arrivalTime, arrivalTime)
                    ? moment(arrivalTime, "HH:mm")?.isValid() ? arrivalTime : ""
                    : undefined,
                additional_wishes: !isEqual(arrivalCardResData?.wishListItems, wishListItems)
                    ? wishListItems : undefined,
                reservation_object_id: reservationObjectId
            })
                .then((res) => {
                    setOperationLoading(undefined);
                    if (res.status !== 200) {
                        setOperationError({
                            detail: res?.data,
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_CARD,
                        });
                    } else {
                    // update guests in reservation after success
                        const tempGuests = guests?.map(g => {
                            if (g?.in_reservation && !g?.selected) {
                                return {
                                    ...g,
                                    in_reservation: false
                                };
                            }
                            return g;
                        });
                        setGuests(tempGuests);
                        setArrivalCardResData({
                            guests: tempGuests,
                            arrivalTime,
                            wishListItems
                        });
                        setOperationSuccess({
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_CARD,
                            detail: res?.data,
                        });
                    }
                })
                .catch((err) => {
                    setOperationLoading(undefined);
                    setOperationError({
                        detail: err?.response,
                        type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_CARD,
                    });
                });
        }
    };

    const modifyGuestSelection = (guestId, isSelected) => {
        if (isSelected && guests?.filter((guest) => guest?.selected)?.length >= maxGuests) {
            return setOperationError({
                type: ARRIVAL_CARD_OPERATION_TYPE.TOO_MANY_POTENTIAL_GUESTS,
                detail: "too many guests",
            });
        }

        const tempGuests = guests?.map((g) => {
            if (g.object_id === guestId) {
                return {
                    ...g,
                    selected: isSelected,
                };
            }

            return g;
        });
        setGuests(tempGuests);
    };

    const modifyPotentialGuestSelection = (guestId, isSelected) => {
        if (isSelected && guests?.filter((guest) => guest?.potential_guest)?.length >= maxGuests) {
            return setOperationError({
                type: ARRIVAL_CARD_OPERATION_TYPE.TOO_MANY_POTENTIAL_GUESTS,
                detail: "too many guests",
            });
        }

        const tempGuests = guests?.map((g) => {
            if (g.object_id === guestId) {
                return {
                    ...g,
                    potential_guest: isSelected,
                };
            }

            return g;
        });
        setGuests(tempGuests);
    };

    const changeMainGuest = (guestId) => {
        const tempGuests = guests?.map((g) => {
            if (g.object_id === guestId) {
                return {
                    ...g,
                    main_guest: true,
                };
            }

            return {
                ...g,
                main_guest: false,
            };
        });
        setGuests(tempGuests);
    };

    const changeArrivalTime = (value) => {
        setArrivalTime(value);
    };

    const saveArrivalTimeAsync = useCallback((reservationId, reservationObjectId, time) => {
        if (authenticated && initialized && !isEqual(arrivalTime, time) && (Boolean(arrivalTime) || Boolean(time))) {
            setAsyncOperationLoading(prev => [...prev, ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME]);

            save(reservationId, {
                reservation_object_id: reservationObjectId,
                arrival_time: time
            })
                .then((res) => {
                    setAsyncOperationLoading(prev => prev?.filter(item => item !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME));
                    if (res.status !== 200) {
                        setOperationError({
                            detail: res?.data,
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME,
                        });
                    } else {
                        const detail = {
                            prev: arrivalTime,
                            next: time
                        };
                        // update arrival time after success
                        setArrivalTime(time);
                        setOperationSuccess({
                            detail,
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME,
                        });
                    }
                })
                .catch((err) => {
                    setAsyncOperationLoading(prev => prev?.filter(item => item !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME));
                    setOperationError({
                        detail: err?.response,
                        type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME,
                    });
                })
                .finally(() => {
                    setAsyncOperationLoading(prev => prev?.filter(item => item !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_ARRIVAL_TIME));
                });
        }
    }, [initialized, authenticated, arrivalTime, setAsyncOperationLoading, save]);

    const addOrRemoveWishListItem = (wishListItem, value) => {
        if (value) {
            // Set the selected sihotCommentIds
            let sihotCommentIdsToAdd = [];
            wishListItem?.sihotCommentIds?.forEach((sihotServiceId) => {
                if (wishListItems.indexOf(sihotServiceId) === -1) {
                    sihotCommentIdsToAdd.push(sihotServiceId);
                }
            });

            if (sihotCommentIdsToAdd.length > 0) {
                let tempWishListItems = [...wishListItems];
                setWishListItems(tempWishListItems.concat(sihotCommentIdsToAdd));
            }
        } else {
            // Remove the selected sihotCommentIds
            let tempWishListItems = [...wishListItems];
            setWishListItems(
                tempWishListItems.filter((sihotServiceId) => {
                    return wishListItem?.sihotCommentIds.indexOf(sihotServiceId) < 0;
                })
            );
        }
    };

    const savePotentialGuestsToReservation = () => {
        const tempGuests = guests.map((guest) => {
            if (guest?.potential_guest) {
                guest.selected = true;
                guest.in_reservation = true;

                return {
                    ...guest,
                    selected: true,
                    in_reservation: true,
                };
            }

            return {
                ...guest,
                selected: false,
                in_reservation: false,
            };
        });

        setGuests(tempGuests);
    };

    const updateOrCreateCoTraveller = (coTravellerId, payload) => {
        if (coTravellerId) {
            //  Update
            if (initialized && authenticated) {
                setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.UPDATE_CO_TRAVELLER);

                const url = API_PREARRIVAL + API_PREARRIVAL_CO_TRAVELLER + (coTravellerId ? "/" + coTravellerId : "");
                bookingAPI
                    .put(url, payload)
                    .then((res) => {
                        setOperationLoading(undefined);

                        const data = res?.data;
                        if (data) {
                            const tempGuests = guests.map((g) => {
                                if (g?.object_id === coTravellerId) {
                                    return {
                                        ...g,
                                        ...data,
                                    };
                                }

                                return {
                                    ...g,
                                };
                            });

                            setGuests(tempGuests);
                            setOperationSuccess({
                                type: ARRIVAL_CARD_OPERATION_TYPE.UPDATE_CO_TRAVELLER,
                                detail: data,
                            });
                        }
                    })
                    .catch((err) => {
                        setOperationError({
                            type: ARRIVAL_CARD_OPERATION_TYPE.UPDATE_CO_TRAVELLER,
                            detail: err?.response,
                        });
                        setOperationLoading(undefined);
                    });
            }
        } else {
            // create
            if (initialized && authenticated) {
                setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.CREATE_CO_TRAVELLER);
                const url = `${API_PREARRIVAL}${API_PREARRIVAL_CO_TRAVELLERS}`;
                bookingAPI.post(url, payload)
                    .then(res => {
                        setOperationLoading(undefined);
                        const data = res?.data;
                        if (data) {
                            const tempGuests = [...guests];
                            tempGuests.push({
                                ...data,
                                in_reservation: false,
                                selected: Boolean(guests?.filter((guest) => guest?.selected)?.length < maxGuests),
                                potential_guest: false,
                                is_editable: true,
                                is_cotraveller: true
                            });
                            setGuests(tempGuests);

                            setOperationSuccess({
                                type: ARRIVAL_CARD_OPERATION_TYPE.CREATE_CO_TRAVELLER,
                                detail: data
                            });
                        }
                    })
                    .catch(err => {
                        setOperationError({
                            type: ARRIVAL_CARD_OPERATION_TYPE.CREATE_CO_TRAVELLER,
                            detail: err?.respon
                        });
                        setOperationLoading(undefined);
                    });
            }
        }
    };

    const deleteCoTraveller = (coTravellerId) => {
        if (initialized && authenticated) {
            setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.DELETE_CO_TRAVELLER);

            const url = API_PREARRIVAL + API_PREARRIVAL_CO_TRAVELLER + (coTravellerId ? "/" + coTravellerId : "");
            bookingAPI
                .delete(url)
                .then((res) => {
                    const data = res?.data;
                    if (data) {
                        setGuests(guests.filter(g => g.object_id !== coTravellerId));
                        setOperationSuccess({
                            type: ARRIVAL_CARD_OPERATION_TYPE.DELETE_CO_TRAVELLER
                        });
                        setOperationLoading(undefined);
                    }
                    return res.data;
                })
                .catch((err) => {
                    setOperationLoading(undefined);
                    setOperationError({
                        type: ARRIVAL_CARD_OPERATION_TYPE.DELETE_CO_TRAVELLER,
                        detail: err?.response
                    });
                });
        }
    };

    const clearOperationErrors = () => {
        setOperationError();
        setOperationSuccess();
        setOperationLoading();
    };

    const initWishListItems = (items) => {
        setWishListItems(items);
    };

    const saveWishListItemsAsync = useCallback((reservationId, reservationObjectId, items) => {
        if (authenticated && initialized) {
            setAsyncOperationLoading(prev => [...prev, ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST]);

            save(reservationId, {
                additional_wishes: !isEqual(wishListItems, items) ? items : undefined,
                reservation_object_id: reservationObjectId,
            })
                .then((res) => {
                    setAsyncOperationLoading(prev => prev?.filter((op) => op !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST));
                    if (res.status !== 200) {
                        setOperationError({
                            detail: res?.data,
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST,
                        });
                    } else {
                        // update wishListItems after success
                        setOperationSuccess({
                            type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST,
                            detail: {
                                prev: wishListItems,
                                next: items
                            }
                        });
                        setWishListItems(items);
                    }
                })
                .catch((err) => {
                    setAsyncOperationLoading(prev => prev?.filter((op) => op !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST));
                    setOperationError({
                        detail: err?.response,
                        type: ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST,
                    });
                })
                .finally(() => {
                    setAsyncOperationLoading(prev => prev?.filter((op) => op !== ARRIVAL_CARD_OPERATION_TYPE.SAVE_WISHLIST));
                });
        }
    }, [initialized, authenticated, setAsyncOperationLoading, wishListItems, save]);

    const removeGuestFromReservationAsync = useCallback(async (reservationId, reservationObjectId, guestId, metas) => {
        if (initialized && authenticated) {
            setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST);

            const id = new Date().valueOf();
            pushMetadataItem(metas, id);

            const participatingGuestsIds = [];
            for (const g of guests) {
                if (g?.in_reservation && g?.selected && g?.object_id !== guestId) {
                    participatingGuestsIds.push(g?.object_id);
                }
            }

            try {
                const res = await save(reservationId, {
                    reservation_object_id: reservationObjectId,
                    guests: participatingGuestsIds,
                    main_guest: guests?.find((g) => g.main_guest)?.object_id.toString(),
                });

                if (res?.status !== 200) {
                    setOperationError({
                        detail: res?.data,
                        type: ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST,
                    });
                }
                setOperationSuccess({
                    type: ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST,
                    detail: res?.data
                });
                setGuests(prev => prev?.map((guest) => {
                    if (guest?.object_id === guestId) {
                        return {
                            ...guest,
                            selected: false,
                            in_reservation: false,
                            position: undefined
                        };
                    }
                    return guest;
                }));
            } catch (err) {
                setOperationError({
                    detail: err?.response,
                    type: ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST,
                });
            } finally {
                removeMetadataItem(id);
                setOperationLoading(undefined);
            }
        }
    }, [
        initialized,
        authenticated,
        guests,
        setGuests,
        pushMetadataItem,
        removeMetadataItem,
        save
    ]);

    const addGuestToReservationAsync = useCallback(async (
        reservationId, reservationObjectId, guestId, position, metas
    ) => {
        if (initialized && authenticated) {
            setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST);

            const id = new Date().valueOf();
            pushMetadataItem(metas, id);

            const participatingGuestsIds = [];
            for (const g of guests) {
                if (g?.in_reservation && g?.selected) {
                    participatingGuestsIds.push(g?.object_id);
                }
            }

            try {
                const res = await save(reservationId, {
                    reservation_object_id: reservationObjectId,
                    guests: [...participatingGuestsIds, guestId],
                    main_guest: guests?.find((g) => g.main_guest)?.object_id.toString(),
                });

                if (res?.status !== 200) {
                    setOperationError({
                        detail: res?.data,
                        type: ARRIVAL_CARD_OPERATION_TYPE.ADD_GUEST,
                    });
                }
                setOperationSuccess({
                    type: ARRIVAL_CARD_OPERATION_TYPE.ADD_GUEST,
                    detail: res?.data
                });
                setGuests(prev => prev?.map((guest) => {
                    if (guest?.object_id === guestId) {
                        return {
                            ...guest,
                            selected: true,
                            in_reservation: true,
                            position
                        };
                    }
                    return guest;
                }));
            } catch (err) {
                setOperationError({
                    detail: err?.response,
                    type: ARRIVAL_CARD_OPERATION_TYPE.REMOVE_GUEST,
                });
            } finally {
                removeMetadataItem(id);
                setOperationLoading(undefined);
            }
        }
    }, [
        initialized,
        authenticated,
        guests,
        setGuests,
        pushMetadataItem,
        removeMetadataItem,
        save
    ]);

    const changeMainGuestAsync = useCallback(async(
        reservationId, reservationObjectId, newGuestId, metas, sendBookingConfirmationEmail = false
    ) => {
        if (initialized && authenticated) {
            setOperationLoading(ARRIVAL_CARD_OPERATION_TYPE.CHANGE_MAIN_GUEST);

            const id = new Date().valueOf();
            pushMetadataItem(metas, id);

            const participatingGuestsIds = [];
            for (const g of guests) {
                if (g?.in_reservation && g?.selected && !g?.main_guest) {
                    participatingGuestsIds.push(g?.object_id);
                }
            }

            try {
                const res = await save(reservationId, {
                    reservation_object_id: reservationObjectId,
                    guests: [...participatingGuestsIds, newGuestId],
                    main_guest: newGuestId
                });

                if (res?.status !== 200) {
                    setOperationError({
                        detail: res?.data,
                        type: ARRIVAL_CARD_OPERATION_TYPE.CHANGE_MAIN_GUEST,
                    });
                } else if (sendBookingConfirmationEmail) {
                    sendEmailToGuest(reservationId, newGuestId);
                }
                setOperationSuccess({
                    detail: res?.data,
                    type: ARRIVAL_CARD_OPERATION_TYPE.CHANGE_MAIN_GUEST,
                });
                setGuests(prev => prev?.map((guest) => {
                    // Remove old main guest from reservation
                    if (guest?.main_guest) {
                        return {
                            ...guest,
                            main_guest: false,
                            in_reservation: false,
                            selected: false,
                            position: undefined
                        };
                    }
                    // Add new main guest to reservation
                    if (guest?.object_id === newGuestId) {
                        return {
                            ...guest,
                            main_guest: true,
                            in_reservation: true,
                            selected: true,
                            position: 0 // 0 is always main guest
                        };
                    }

                    return {
                        ...guest,
                        main_guest: false
                    };
                }));
            } catch (err) {
                setOperationError({
                    detail: err?.response,
                    type: ARRIVAL_CARD_OPERATION_TYPE.CHANGE_MAIN_GUEST,
                });
            } finally {
                removeMetadataItem(id);
                setOperationLoading(undefined);
            }
        }
    }, [
        initialized,
        authenticated,
        guests,
        setGuests,
        pushMetadataItem,
        removeMetadataItem,
        save
    ]);

    const resetPotentialGuests = () => {
        const tempGuests = guests?.map(g => {
            return {
                ...g,
                potential_guest: false
            };
        });

        for (const guest of tempGuests) {
            if (guest?.selected) {
                guest.potential_guest = true;
            }
        }

        setGuests(tempGuests);
    };

    const instance = useMemo(
        () => ({
            guests,
            arrivalTime,
            wishListItems,
            initLoading,
            operationLoading,
            operationError,
            operationMetadata,
            operationSuccess,
            initError,
            shareholder: guests?.find(g => g.member_id),
            asyncOperationsLoading,
            ARRIVAL_CARD_OPERATION_TYPE,
            GUEST_ACTION,
            initArrivalCard,
            modifyGuestSelection,
            modifyPotentialGuestSelection,
            changeMainGuest,
            changeArrivalTime,
            addOrRemoveWishListItem,
            saveArrivalCard,
            savePotentialGuestsToReservation,
            updateOrCreateCoTraveller,
            deleteCoTraveller,
            clearOperationErrors,
            resetPotentialGuests,
            initWishListItems,
            saveWishListItemsAsync,
            removeGuestFromReservationAsync,
            addGuestToReservationAsync,
            changeMainGuestAsync,
            saveArrivalTimeAsync,
        }),
        [
            guests,
            arrivalTime,
            wishListItems,
            initLoading,
            operationError,
            operationSuccess,
            operationLoading,
            operationMetadata,
            initError,
            asyncOperationsLoading,
            initArrivalCard,
            modifyGuestSelection,
            modifyPotentialGuestSelection,
            changeMainGuest,
            changeArrivalTime,
            saveArrivalCard,
            updateOrCreateCoTraveller,
            deleteCoTraveller,
            resetPotentialGuests,
            initWishListItems,
            saveWishListItemsAsync,
            saveWishListItemsAsync,
            removeGuestFromReservationAsync,
            addGuestToReservationAsync,
            changeMainGuestAsync,
            saveArrivalTimeAsync,
        ]
    );

    return instance;
};
